import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, DollarSign, Activity, Lock, Coins } from "lucide-react";
import { getApiBase } from "../config";
import { clearAuth, getToken } from "../auth";

const API_BASE_URL = getApiBase(); // Use the helper function
interface OrgDetail {
  id: string;
  name: string;
  billing_email: string;
  current_balance: number;
  created_at: string;
}

interface Log {
  event_type: string;
  description: string | null;
  delta_tokens: number;
  balance_after: number;
  created_at: string;
}

export default function OrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [logs, setLogs] = useState<Log[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Spendable balance from the server (the shared SuitPilot wallet once billing is
  // on it); org.current_balance is iRedact's own ledger, which stops moving then.
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [toastTimer, setToastTimer] = useState<number | null>(null);
  
  // Refund State
  const [adjustAmount, setAdjustAmount] = useState<number | "">("");
  const [adjustReason, setAdjustReason] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [ownerPasswordConfirm, setOwnerPasswordConfirm] = useState("");

  // Per-org token rates (tokens charged per page)
  const [rates, setRates] = useState<{ redact: number | ""; split: number | ""; promissory_note: number | "" }>({
    redact: "",
    split: "",
    promissory_note: "",
  });

  const fetchDetails = async () => {
    try {
      const token = getToken();
      if (!token) {
        clearAuth();
        navigate("/login");
        return;
      }
      const res = await fetch(`${API_BASE_URL}/admin/org/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        clearAuth();
        navigate("/login");
        return;
      }
      const data = await res.json();
      setOrg(data.org);
      setWalletBalance(typeof data.wallet_balance === "number" ? data.wallet_balance : null);
      setLogs(data.history || []);
      const tr = data.token_rates || {};
      setRates({
        redact: tr.REDACT ?? "",
        split: tr.SPLIT ?? "",
        promissory_note: tr.PROMISSORY_NOTE ?? "",
      });
      setIsLoading(false);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    if (toastTimer) window.clearTimeout(toastTimer);
    const t = window.setTimeout(() => setToast(null), 3500);
    setToastTimer(t);
    return () => window.clearTimeout(t);
  }, [toast]);

  const handleAdjustment = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) {
      clearAuth();
      navigate("/login");
      return;
    }
    if (adjustAmount === "" || !adjustReason.trim()) {
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/admin/org/${id}/adjustment`, {
          method: "POST",
          headers: { 
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ amount: adjustAmount, reason: adjustReason.trim() })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = body?.detail || "Adjustment failed";
        setToast({ type: "error", message: msg });
        return;
      }
      setAdjustAmount("");
      setAdjustReason("");
      setToast({ type: "success", message: "Balance adjusted successfully." });
      fetchDetails();
    } catch (err) {
      console.error(err);
      setToast({ type: "error", message: "Network error while adjusting balance." });
    }
  };

  const handleSetRates = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) {
      clearAuth();
      navigate("/login");
      return;
    }
    if (rates.redact === "" || rates.split === "" || rates.promissory_note === "") {
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/admin/org/${id}/token-rates`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          redact: Number(rates.redact),
          split: Number(rates.split),
          promissory_note: Number(rates.promissory_note),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setToast({ type: "error", message: body?.detail || "Failed to update rates" });
        return;
      }
      setToast({ type: "success", message: "Token rates updated." });
      fetchDetails();
    } catch (err) {
      console.error(err);
      setToast({ type: "error", message: "Network error while updating rates." });
    }
  };

  const handleOwnerPasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = getToken();
    if (!token) {
      clearAuth();
      navigate("/login");
      return;
    }
    if (!ownerPassword || ownerPassword.length < 8) {
      setToast({ type: "error", message: "Password must be at least 8 characters." });
      return;
    }
    if (ownerPassword !== ownerPasswordConfirm) {
      setToast({ type: "error", message: "Password confirmation does not match." });
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}/admin/org/${id}/owner/password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ new_password: ownerPassword }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const msg = body?.detail || "Owner password update failed";
        setToast({ type: "error", message: msg });
        return;
      }
      setOwnerPassword("");
      setOwnerPasswordConfirm("");
      setToast({ type: "success", message: "Owner password updated successfully." });
    } catch (err) {
      console.error(err);
      setToast({ type: "error", message: "Network error while updating password." });
    }
  };

  if (isLoading || !org) return <div className="p-8 dark:text-white">Loading Client Data...</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {toast && (
        <div className="fixed top-6 right-6 z-50">
          <div
            className={`px-4 py-3 rounded-lg shadow-lg border text-sm font-medium ${
              toast.type === "success"
                ? "bg-green-50 text-green-800 border-green-200"
                : "bg-red-50 text-red-800 border-red-200"
            }`}
          >
            {toast.message}
          </div>
        </div>
      )}
      {/* NAV */}
      <button onClick={() => navigate('/admin')} className="flex items-center text-slate-500 hover:text-blue-500 mb-6 transition">
        <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
      </button>

      {/* HEADER CARD */}
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-8 mb-8 flex justify-between items-center">
        <div>
            <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{org.name}</h1>
            </div>
            <p className="text-slate-500">{org.billing_email} • Member since {new Date(org.created_at).toLocaleDateString()}</p>
        </div>
        <div className="text-right">
            <p className="text-sm text-slate-500 uppercase tracking-wider font-bold">Token Balance</p>
            <p className="text-4xl font-bold text-blue-600 dark:text-blue-400">{(walletBalance ?? org.current_balance).toLocaleString()}</p>
            {walletBalance !== null && walletBalance !== org.current_balance && (
              <p className="text-xs text-slate-500 mt-1">Shared SuitPilot wallet (all products)</p>
            )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* LEFT COLUMN: HISTORY */}
        <div className="lg:col-span-2 space-y-8">
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
                <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2">
                    <Activity size={20} className="text-slate-400"/>
                    <h3 className="font-bold text-slate-900 dark:text-white">Audit Log & Transactions</h3>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {logs.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                      No ledger entries yet.
                    </div>
                  ) : (
                    <table className="w-full text-left text-sm">
                      <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 font-medium">
                        <tr>
                          <th className="p-4">Type</th>
                          <th className="p-4">Details</th>
                          <th className="p-4 text-right">Amount</th>
                          <th className="p-4 text-right">Balance</th>
                          <th className="p-4 text-right">Time</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {logs.map((log, i) => (
                          <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                            <td className="p-4 font-medium dark:text-slate-200">{log.event_type}</td>
                            <td className="p-4 text-slate-500 truncate max-w-xs">{log.description || "-"}</td>
                            <td className={`p-4 text-right font-mono ${log.delta_tokens >= 0 ? "text-green-600" : "text-red-600"}`}>
                              {log.delta_tokens >= 0 ? "+" : ""}{log.delta_tokens}
                            </td>
                            <td className="p-4 text-right font-mono text-slate-600 dark:text-slate-300">
                              {log.balance_after}
                            </td>
                            <td className="p-4 text-right text-slate-400">{new Date(log.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
            </div>
        </div>

        {/* RIGHT COLUMN: ACTIONS */}
        <div className="space-y-8">
            
            {/* REFUND / ADJUSTMENT */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center gap-2 mb-4 text-slate-900 dark:text-white font-bold">
                    <DollarSign size={20} className="text-blue-500"/>
                    <h3>Manual Adjustment</h3>
                </div>
                <form onSubmit={handleAdjustment} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Tokens (+/-)</label>
                    <input 
                        type="number" 
                        className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded p-2 dark:text-white"
                        placeholder="e.g. 500 or -200"
                        value={adjustAmount}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "") {
                            setAdjustAmount("");
                            return;
                          }
                          const next = Number(v);
                          setAdjustAmount(Number.isFinite(next) ? next : "");
                        }}
                    />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Reason</label>
                        <input 
                            type="text" 
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded p-2 dark:text-white"
                            placeholder="Refund for failed scan"
                            value={adjustReason}
                            onChange={e => setAdjustReason(e.target.value)}
                        />
                    </div>
                    <button
                      disabled={adjustAmount === "" || !adjustReason.trim()}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-2 rounded text-sm transition"
                    >
                        Process Transaction
                    </button>
                </form>
            </div>

            {/* TOKEN RATES (tokens charged per page) */}
            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center gap-2 mb-1 text-slate-900 dark:text-white font-bold">
                    <Coins size={20} className="text-blue-500"/>
                    <h3>Token Rates</h3>
                </div>
                <p className="text-xs text-slate-500 mb-4">Tokens charged per page for this organization. Redact + Split = the combined rate automatically.</p>
                <form onSubmit={handleSetRates} className="space-y-4">
                    {([
                      ["Redaction", "redact"],
                      ["Split", "split"],
                      ["Promissory Note", "promissory_note"],
                    ] as const).map(([label, key]) => (
                      <div key={key}>
                        <label className="block text-xs font-bold text-slate-500 mb-1">{label} (tokens / page)</label>
                        <input
                          type="number"
                          min={0}
                          className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded p-2 dark:text-white"
                          value={rates[key]}
                          onChange={(e) => {
                            const v = e.target.value;
                            setRates((prev) => ({ ...prev, [key]: v === "" ? "" : Math.max(0, Number(v)) }));
                          }}
                        />
                      </div>
                    ))}
                    <button
                      disabled={rates.redact === "" || rates.split === "" || rates.promissory_note === ""}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-2 rounded text-sm transition"
                    >
                        Save Rates
                    </button>
                </form>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6">
                <div className="flex items-center gap-2 mb-4 text-slate-900 dark:text-white font-bold">
                    <Lock size={20} className="text-blue-500"/>
                    <h3>Reset Owner Password</h3>
                </div>
                <form onSubmit={handleOwnerPasswordChange} className="space-y-4">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">New Password</label>
                        <input
                            type="password"
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded p-2 dark:text-white"
                            placeholder="Minimum 8 characters"
                            value={ownerPassword}
                            onChange={(e) => setOwnerPassword(e.target.value)}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 mb-1">Confirm Password</label>
                        <input
                            type="password"
                            className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded p-2 dark:text-white"
                            placeholder="Re-enter new password"
                            value={ownerPasswordConfirm}
                            onChange={(e) => setOwnerPasswordConfirm(e.target.value)}
                        />
                    </div>
                    <button
                      disabled={!ownerPassword || !ownerPasswordConfirm}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-2 rounded text-sm transition"
                    >
                        Update Owner Password
                    </button>
                </form>
            </div>
        </div>
      </div>
    </div>
  );
}
